/**
 * Prescription file upload with drag-and-drop, list, and removal.
 */

function getConfig() {
	return window.doneCommerce || {};
}

async function apiFetch(path, options = {}) {
	const config = getConfig();
	const response = await fetch(`${config.restUrl}${path}`, {
		...options,
		headers: {
			'X-WP-Nonce': config.nonce,
			...(options.headers || {}),
		},
	});

	if (!response.ok) {
		throw new Error('Request failed');
	}

	return response.json();
}

function escapeHtml(value) {
	return String(value ?? '')
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;');
}

function fileIcon(mime) {
	if (String(mime).startsWith('image/')) {
		return '🖼';
	}
	if (mime === 'application/pdf') {
		return '📄';
	}
	return '📎';
}

function renderFileList(container, files) {
	const list = container.querySelector('[data-prescription-list]');
	if (!list) {
		return;
	}

	if (!files?.length) {
		list.innerHTML = '';
		return;
	}

	list.innerHTML = files
		.map(
			(file) => `
		<li class="dc-prescription-upload__file">
			<span class="dc-prescription-upload__file-thumb" aria-hidden="true">${fileIcon(file.mime)}</span>
			<span class="dc-prescription-upload__file-name">${escapeHtml(file.original_name || file.filename || 'Prescription')}</span>
			<button type="button" class="dc-prescription-upload__file-remove" data-prescription-remove data-file-id="${escapeHtml(file.id)}">${getConfig().i18n?.removeItem || 'Remove'}</button>
		</li>
	`
		)
		.join('');
}

async function refreshFileList(container) {
	try {
		const data = await apiFetch('done-commerce/v1/prescription');
		renderFileList(container, data.files || []);
	} catch {
		renderFileList(container, []);
	}
}

async function uploadFile(file, container) {
	const config = getConfig();
	const progressBar = container.querySelector('.dc-prescription-upload__progress-bar');
	const status = container.querySelector('.dc-prescription-upload__status');
	const progress = container.querySelector('.dc-prescription-upload__progress');

	progress?.removeAttribute('hidden');
	status.textContent = config.i18n?.uploading || 'Uploading…';
	status.className = 'dc-prescription-upload__status';

	const formData = new FormData();
	formData.append('file', file);

	const orderId = container.dataset.orderId;
	if (orderId) {
		formData.append('order_id', orderId);
	}

	try {
		const xhr = new XMLHttpRequest();
		xhr.open('POST', `${config.restUrl}done-commerce/v1/prescription`);
		xhr.setRequestHeader('X-WP-Nonce', config.nonce);

		xhr.upload.addEventListener('progress', (e) => {
			if (e.lengthComputable && progressBar) {
				progressBar.style.width = `${Math.round((e.loaded / e.total) * 100)}%`;
			}
		});

		await new Promise((resolve, reject) => {
			xhr.onload = () => {
				if (xhr.status >= 200 && xhr.status < 300) {
					resolve(JSON.parse(xhr.responseText));
				} else {
					reject(new Error('Upload failed'));
				}
			};
			xhr.onerror = reject;
			xhr.send(formData);
		});

		status.textContent = config.i18n?.uploadComplete || 'Upload complete';
		status.classList.add('dc-prescription-upload__status--success');
		await refreshFileList(container);
	} catch {
		status.textContent = config.i18n?.uploadFailed || 'Upload failed. Please try again.';
		status.classList.add('dc-prescription-upload__status--error');
	} finally {
		if (progressBar) {
			progressBar.style.width = '0%';
		}
		progress?.setAttribute('hidden', '');
	}
}

export function initPrescriptionUpload() {
	document.querySelectorAll('[data-prescription-upload]').forEach((container) => {
		const input = container.querySelector('.dc-prescription-upload__input');
		const dropzone = container.querySelector('.dc-prescription-upload') || container;

		if (!input) {
			return;
		}

		refreshFileList(container);

		const handleFiles = (files) => {
			Array.from(files).forEach((file) => uploadFile(file, container));
		};

		input.addEventListener('change', () => handleFiles(input.files));

		['dragenter', 'dragover'].forEach((event) => {
			dropzone.addEventListener(event, (e) => {
				e.preventDefault();
				dropzone.classList.add('is-dragover');
			});
		});

		['dragleave', 'drop'].forEach((event) => {
			dropzone.addEventListener(event, (e) => {
				e.preventDefault();
				dropzone.classList.remove('is-dragover');
			});
		});

		dropzone.addEventListener('drop', (e) => {
			handleFiles(e.dataTransfer?.files || []);
		});

		container.addEventListener('click', async (e) => {
			const removeBtn = e.target.closest('[data-prescription-remove]');
			if (!removeBtn) {
				return;
			}

			const fileId = removeBtn.dataset.fileId;
			if (!fileId) {
				return;
			}

			try {
				await apiFetch(`done-commerce/v1/prescription?file_id=${encodeURIComponent(fileId)}`, {
					method: 'DELETE',
				});
				await refreshFileList(container);
			} catch {
				const status = container.querySelector('.dc-prescription-upload__status');
				if (status) {
					status.textContent = getConfig().i18n?.removeFailed || 'Unable to remove file';
					status.classList.add('dc-prescription-upload__status--error');
				}
			}
		});
	});
}
